import PropTypes from 'prop-types';
import React, { useMemo, useReducer } from 'react';
import { useLocation, useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { CARD_FIELDS_SNIPPET_KEYS } from '../../../constants/sdk';
import IntegrationPageSectionContext from './IntegrationPageSectionContext';
import IntegrationPageSectionReducer from './IntegrationPageSectionReducer';
import updateConsolidatedCode from './dynamicCodeSnippetsHelper';
import getDownloadUrl from '../getDownloadUrl';
import { queryParamtoValueMap } from '../ControlPanel/constants';
import { useSelector } from 'react-redux';
import { previewSelector } from '../../../store/App/selectors';

export default function IntegrationPageSectionProvider({
  children,
  ecmContent = [],
  serviceData: { apps = [], clientAccessToken, defaultClientId } = {},
  sdkBaseUrl,
  pageMetaDataConfig,
  isUserLoggedIn = false
}) {
  const { product } = useParams();
  const [queryParams] = useSearchParams();
  const typeQueryParam = queryParams.get('type');
  const geoLocOverrideQueryParam = queryParams.get('geoLocOverride');
  const url = useLocation().pathname;
  const isPreview = useSelector(previewSelector);

  const fetchEcmContent = async (frontend, backend) => {
    const baseUrl = '/studio/api/v1/ecm/data/integration';
    const urlObject = new URL(baseUrl, window.location.origin);

    urlObject.searchParams.append('url', url);
    urlObject.searchParams.append('frontend', frontend);
    urlObject.searchParams.append('backend', backend);

    if (isPreview) {
      urlObject.searchParams.append('mode', 'preview');
    }

    const response = await fetch(urlObject.toString());

    return response;
  };

  // there is potential here to move this initial state creation to the backend possibly.
  const jsonConfiguration =
    useMemo(
      () =>
        ecmContent.contents
          ?.filter((entry) => entry.contentJson)
          .map((entry) => entry.contentJson)
          .reduce((acc, curr) => ({ ...acc, ...curr }), {}),
      []
    ) || {};

  const getCodeConfigurations = (environment, codes) =>
    codes
      ?.map(
        (entry) =>
          entry[
            environment.toLowerCase() === 'frontend'
              ? 'codeFileMappings'
              : 'backendCodeFileMappings'
          ]
      )
      .flat()
      .filter((val) => val);

  const frontendCodeConfigurations =
    useMemo(() => getCodeConfigurations('frontend', ecmContent.codes), []) || [];

  const backendCodeConfigurations =
    useMemo(() => getCodeConfigurations('backend', ecmContent.codes), []) || [];

  const defaultCodeStatus = {
    ...frontendCodeConfigurations.reduce((acc, curr) => {
      return {
        ...acc,
        [curr.key]: curr.active
      };
    }, {}),
    ...backendCodeConfigurations.reduce((acc, curr) => {
      return {
        ...acc,
        [curr.key]: curr.active
      };
    }, {})
  };

  const defaultLanguageValues = {
    frontend: queryParams.get('frontend') || 'html',
    backend:
      queryParamtoValueMap[queryParams.get('backend')] ||
      queryParams.get('backend') ||
      'javascript',
    status: defaultCodeStatus
  };

  const {
    SdkConfigurator = {},
    OrderConfigurator = {},
    ButtonConfigurator = {},
    TypeConfigurator = [''],
    VaultSetupTokenConfigurator = {},
    FundingConfigurator = null,
    AppSwitch,
    GeoLocOverride = []
  } = jsonConfiguration;

  const type = TypeConfigurator.some((i) => i.id === typeQueryParam)
    ? typeQueryParam
    : TypeConfigurator[0].id;

  const geoLocOverride = GeoLocOverride.some((geo) => geo.value === geoLocOverrideQueryParam)
    ? geoLocOverrideQueryParam
    : GeoLocOverride[0]?.value;

  const fundingOptions = FundingConfigurator
    ? FundingConfigurator?.options?.filter((opt) => !opt.disabled).map((opt) => opt.value)
    : [];

  // for standard checkout we need to take the client id from ECM.
  // we need the separate client id to maintain venmo eligibility for logged out experience
  let clientId;
  if (isUserLoggedIn) {
    const appQueryParam = queryParams.get('app');
    if (appQueryParam) {
      clientId = apps.find((val) => val.name.replaceAll(' ', '_') === appQueryParam)?.clientId;
    }
    clientId = clientId || apps[0]?.clientId;
    // Fallback for clientId if not found in apps array
    if (!clientId) {
      clientId = product === 'fastlane' ? defaultClientId : SdkConfigurator.sdkSettings?.clientId;
    }
  } else if (product !== 'fastlane') {
    // for standard and acdc products we need to use the clientId from ECM
    // so that we are able to load venmo button on frontend
    clientId = SdkConfigurator.sdkSettings?.clientId;
  } else {
    // for fastlane integration we need to use the clientId from the backend integration
    clientId = defaultClientId;
  }

  const intent = SdkConfigurator.sdkSettings?.intent || 'capture'; // default the intent
  const currency = SdkConfigurator.sdkSettings?.currency || 'USD'; // default the currency
  const enableFunding = SdkConfigurator.sdkSettings?.enableFunding || []; // default the enabled funding sources
  const disableFunding = SdkConfigurator.sdkSettings?.disableFunding || []; // default the disabled funding sources

  const sdkSettings = {
    ...SdkConfigurator?.sdkSettings,
    sdkBaseUrl,
    clientId,
    clientAccessToken,
    intent,
    currency,
    enableFunding: Array.from(new Set([...fundingOptions, ...enableFunding])).join(','),
    disableFunding: disableFunding.join(',')
  };

  const defaultCardFieldsConfigurator = {
    value: {
      [CARD_FIELDS_SNIPPET_KEYS.PARENT]: {
        input: {
          'font-size': '16px',
          'font-family': 'courier, monospace',
          'font-weight': 'lighter',
          color: '#ccc'
        },
        '.invalid': {
          color: 'purple'
        }
      },
      [CARD_FIELDS_SNIPPET_KEYS.NAME]: {
        input: { color: 'blue' },
        '.invalid': { color: 'purple' }
      },
      [CARD_FIELDS_SNIPPET_KEYS.NUMBER]: { input: { color: 'blue' } },
      [CARD_FIELDS_SNIPPET_KEYS.EXPIRY_DATE]: { input: { color: 'blue' } },
      [CARD_FIELDS_SNIPPET_KEYS.CVV]: { input: { color: 'blue' } }
    }
  };

  const vaultSetupTokenSettings = {
    ...VaultSetupTokenConfigurator,
    name: 'Company',
    selectedUsagePattern: VaultSetupTokenConfigurator?.usagePattern?.options[0]?.value || null,
    selectedFrequency: VaultSetupTokenConfigurator?.frequency?.options[2]?.value || [
      { label: 'Monthly', value: 'MONTH' }
    ],
    intervalCount: '1',
    totalCycles: '1',
    startDate: new Date(),
    oneTimeCharges: {
      setupFee: '0',
      shippingAmount: '0',
      taxes: '0',
      productPrice: '10'
    },
    paymentTokens: { options: [], selected: '' }
  };
  const [state, dispatch] = useReducer(IntegrationPageSectionReducer, {
    ...jsonConfiguration,
    SdkConfigurator: {
      ...SdkConfigurator,
      sdkSettings
    },
    integrationType: type,
    geoLocOverride: geoLocOverride,
    ShippingCallbackConfigurator: {
      shippingCallbackType: 'client'
    },
    code: {
      frontend: frontendCodeConfigurations,
      backend: backendCodeConfigurations,
      consolidatedCode: useMemo(
        () =>
          updateConsolidatedCode(
            frontendCodeConfigurations,
            backendCodeConfigurations,
            sdkSettings,
            defaultLanguageValues,
            OrderConfigurator,
            ButtonConfigurator,
            defaultCardFieldsConfigurator,
            product,
            type,
            vaultSetupTokenSettings,
            geoLocOverride
          ),
        []
      ),
      language: {
        frontend: defaultLanguageValues.frontend,
        backend: defaultLanguageValues.backend
      },
      status: defaultCodeStatus,
      downloadCodeUrl: useMemo(
        () =>
          getDownloadUrl({
            frontend: defaultLanguageValues.frontend,
            backend: defaultLanguageValues.backend,
            product,
            pageContent: ecmContent.contents,
            type: type
          }),
        []
      )
    },
    codeHighlight: {
      codeBlock: {},
      currentSectionKey: undefined
    },
    lastCapture: { value: null },
    apps,
    cardFieldsConfigurator: defaultCardFieldsConfigurator,
    product,
    pageMetaDataConfig,
    appSwitch: AppSwitch ?? false,
    VaultSetupTokenConfigurator: vaultSetupTokenSettings
  });

  // TODO: extract this value into memo object
  return (
    <IntegrationPageSectionContext.Provider
      value={{
        ...state,
        ButtonConfigurator: {
          ...state.ButtonConfigurator,
          updateButtonSettings: (value, category) =>
            dispatch({ type: 'UPDATE_BUTTON_SETTINGS', value, category })
        },
        ShippingCallbackConfigurator: {
          ...state.ShippingCallbackConfigurator,
          updateShippingCallbackSelection: (value) => {
            dispatch({ type: 'UPDATE_SHIPPING_CALLBACK_SELECTION', value });
          }
        },
        SdkConfigurator: {
          ...state.SdkConfigurator,
          updateSdkSettings: (value, category) => {
            dispatch({ type: 'UPDATE_SDK_SETTINGS', value, category });
            if (category === 'type') {
              dispatch({
                type: 'UPDATE_DOWNLOAD_URL',
                value: getDownloadUrl({
                  frontend: state.code.language.frontend,
                  backend: state.code.language.backend,
                  pageContent: state.pageContent || ecmContent.contents,
                  product,
                  type: value
                })
              });
            }
          },
          batchUpdateSdkSettings: (value) => {
            dispatch({ type: 'BATCH_UPDATE_SDK_SETTINGS', value });
          }
        },
        code: {
          ...state.code,
          updateCodeSelection: async (value, category) => {
            const { frontend, backend } = {
              ...state.code.language,
              [category]: value
            };
            dispatch({ type: 'UPDATE_LOADING_STATE', value: true });
            const response = await fetchEcmContent(frontend, backend);
            const result = await response.json();
            dispatch({ type: 'UPDATE_LOADING_STATE', value: false });

            if (response.ok) {
              dispatch({
                type: 'UPDATE_CODE_SELECTION',
                value,
                category,
                contents: result.data.contents,
                codes: {
                  frontend: getCodeConfigurations('frontend', result.data.codes),
                  backend: getCodeConfigurations('backend', result.data.codes)
                },
                downloadCodeUrl: getDownloadUrl({
                  frontend,
                  backend,
                  pageContent: result.data.contents,
                  product,
                  type: type
                })
              });
            }
          },
          updateHighlightedCode: (codeBlock) => {
            dispatch({ type: 'UPDATE_HIGHLIGHTED_CODEBLOCK', codeBlock });
          },
          updateCurrentSectionKey: (currentSectionKey) => {
            dispatch({ type: 'UPDATE_CURRENT_SECTION_KEY', currentSectionKey });
          },
          updateActiveCodeBlock: ({ key, value }) => {
            dispatch({ type: 'TOGGLE_CODE_BLOCK', key, value });
          },
          updateAppSwitch: (value) => {
            dispatch({ type: 'UPDATE_APP_SWITCH', value });
          }
        },
        OrderConfigurator: {
          ...state.OrderConfigurator,
          updateAmount: (value, category) => dispatch({ type: 'UPDATE_AMOUNT', value, category }),
          update3dsSetting: (value) => dispatch({ type: 'UPDATE_3DS_SETTING', value }),
          updateSellerId: (value) => dispatch({ type: 'UPDATE_SELLER_ID', value }),
          updateBuyerEmailAddress: (value) =>
            dispatch({ type: 'UPDATE_BUYER_EMAIL_ADDRESS', value }),
          updateContactModulePreference: (value) =>
            dispatch({ type: 'UPDATE_CONTACT_MODULE_PREFERENCE', value })
        },
        VaultSetupTokenConfigurator: {
          ...state.VaultSetupTokenConfigurator,
          updateVaultSetupOption: (value, key) =>
            dispatch({ type: 'UPDATE_VAULT_SETUP_OPTION', value, key }),
          addPaymentToken: (value) => dispatch({ type: 'ADD_PAYMENT_TOKEN', value })
        },
        lastCapture: {
          ...state.lastCapture,
          updateLastCapture: (value) => dispatch({ type: 'UPDATE_LAST_CAPTURE', value })
        },
        cardFieldsConfigurator: {
          ...state.cardFieldsConfigurator,
          updateCardFieldStyles: (value) => dispatch({ type: 'UPDATE_CARD_FIELDS_STYLES', value })
        },
        fastlaneContext: {
          ...state.fastlaneContext,
          setFastlaneContext: (value, category) =>
            dispatch({ type: 'UPDATE_FASTLANE_CONTEXT', value, category })
        }
      }}
    >
      {children}
    </IntegrationPageSectionContext.Provider>
  );
}

IntegrationPageSectionProvider.propTypes = {
  ecmContent: PropTypes.object.isRequired,
  children: PropTypes.node.isRequired,
  serviceData: PropTypes.objectOf({ apps: PropTypes.array, clientAccessToken: PropTypes.string })
    .isRequired,
  sdkBaseUrl: PropTypes.string.isRequired,
  pageMetaDataConfig: PropTypes.object.isRequired,
  isUserLoggedIn: PropTypes.string.isRequired
};
